import { useRef, useMemo } from 'react'
import type { Account } from '@/common/types'
import {
    AccountSwitchState,
    shouldFinishSwitch,
    type AccountSwitchStateMachineState,
} from './accountSwitchStateMachine'

interface UseAccountSwitchStateMachineProps {
    isAccountSwitching: boolean
    accountSwitchTargetId: string | null
    activeAccountId: string | null
    switchSessionToken: number
    userAccounts: Account[] | undefined
    resolvedPrimaryAccount: Account | null
    isUserPending: boolean
    userError: unknown
}

interface UseAccountSwitchStateMachineResult {
    shouldFinishSwitch: boolean
}

/**
 * Hook that encapsulates the account switch state machine logic.
 * Determines whether a switch operation should be marked as complete.
 */
export function useAccountSwitchStateMachine({
    isAccountSwitching,
    accountSwitchTargetId,
    activeAccountId,
    switchSessionToken,
    userAccounts,
    resolvedPrimaryAccount,
    isUserPending,
    userError,
}: UseAccountSwitchStateMachineProps): UseAccountSwitchStateMachineResult {
    const activeSwitchSessionTokenRef = useRef<number | null>(null)

    if (isAccountSwitching) {
        activeSwitchSessionTokenRef.current = switchSessionToken
    } else {
        activeSwitchSessionTokenRef.current = null
    }

    // Build state machine state
    const stateMachineState: AccountSwitchStateMachineState = useMemo(
        () => ({
            state: isAccountSwitching ? AccountSwitchState.SWITCHING : AccountSwitchState.IDLE,
            targetAccountId: accountSwitchTargetId,
            sessionToken: switchSessionToken,
        }),
        [isAccountSwitching, accountSwitchTargetId, switchSessionToken],
    )

    // Determine if switch should finish
    const shouldFinish = useMemo(() => {
        if (!isAccountSwitching || activeSwitchSessionTokenRef.current === null) {
            return false
        }

        const context = {
            currentAccountId: activeAccountId,
            targetAccountId: accountSwitchTargetId,
            availableAccounts: userAccounts ?? [],
            primaryAccount: resolvedPrimaryAccount,
            sessionToken: switchSessionToken,
            isUserPending,
            userError,
        }

        return shouldFinishSwitch(stateMachineState, context, activeSwitchSessionTokenRef.current)
    }, [
        isAccountSwitching,
        stateMachineState,
        activeAccountId,
        accountSwitchTargetId,
        userAccounts,
        resolvedPrimaryAccount,
        switchSessionToken,
        isUserPending,
        userError,
    ])

    return {
        shouldFinishSwitch: shouldFinish,
    }
}
